import React, { useState, useEffect, useRef, useCallback } from 'react';
import { masterSupabase } from '../../lib/supabase-master.js';
import { Sun, Moon, Bell, ChevronDown, Mic, Home, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVoiceConsent } from '../../hooks/useVoiceConsent';
import VoiceConsentModal from '../VoiceConsentModal';
import { useNavigate } from 'react-router-dom';

import HybridIntelligence from "../../services/HybridIntelligence.js";
import { useTranslation } from 'react-i18next';


// UUID validation helper function
const isValidUUID = (str) => {
  if (!str || str === 'all' || str === 'null' || str === 'undefined' || str === '' || str === '0') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const ClaraWelcomeHeader = ({ selectedOwner, setSelectedOwner, owners, selectedProperty, setSelectedProperty, properties, setProperties }) => {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();

  // Fetch urgent actions count
  useEffect(() => {
    const fetchUrgentActions = async () => {
      try {
        const response = await fetch('/api/actions/today');
        if (response.ok) {
          const data = await response.json();
          const urgent = data.filter(a => a.action_type === 'escalate' || a.action_type === 'remind_standard');
          setUrgentActions(urgent);
          setUrgentActionCount(urgent.length);
        }
      } catch (error) {
        console.error("Failed to fetch urgent actions:", error);
      }
    };
    fetchUrgentActions();
    // Optional: Set up an interval to poll for new actions every 5 minutes
    const interval = setInterval(fetchUrgentActions, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Update display names when language changes
  useEffect(() => {
    if (selectedOwner === "all") {
      setSelectedOwnerName(t("filters.allOwners"));
    }
    if (selectedProperty === "all") {
      setSelectedPropertyName(t("filters.allProperties"));
    }
  }, [i18n.language, selectedOwner, selectedProperty, t]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedOwnerName, setSelectedOwnerName] = useState(t('filters.allOwners'));
  const [selectedPropertyName, setSelectedPropertyName] = useState(t("filters.allProperties"));
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('de');
  const [urgentActionCount, setUrgentActionCount] = useState(0);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [urgentActions, setUrgentActions] = useState([]);
  
  // Voice and Clara KI states
  const [voiceState, setVoiceState] = useState('idle'); // idle, listening, responding, waiting, asking
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [hasPermission, setHasPermission] = useState(false);
  const [countdownProgress, setCountdownProgress] = useState(0); // 0-100 for countdown ring
  const recognitionRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const waitingTimerRef = useRef(null);
  const claraIntelligenceRef = useRef(HybridIntelligence);

  // Auth and Consent
  const { user } = useAuth();
  const {
    consentStatus,
    isLoading: consentLoading,
    showModal,
    handleAccept,
    handleDecline,
    closeModal,
    hasVoicePermission,
    needsConsent
  } = useVoiceConsent(user?.id);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Load owners and properties data from Supabase (1. August 2025)
  useEffect(() => {
    const loadOwnersAndProperties = async () => {
      try {
        console.log('🔄 Loading owners and properties from Supabase...');
        
        
        // Load properties
        // Only load properties if user is available
        if (!user?.id) {
          console.log("🚫 No user ID available, skipping properties load");
          return;
        }

        const { data: propertiesData, error: propertiesError } = await masterSupabase
          .from("properties")
          .select("id, property_name, owner_id")
          .eq("created_by", user.id)
          .order("property_name");
          
        if (propertiesError) {
          console.error('❌ Error loading properties:', propertiesError);
        } else {
          console.log('✅ Properties loaded:', propertiesData?.length || 0);
          if (setProperties && typeof setProperties === 'function') {
            setProperties(propertiesData || []);
          }
        }
        
      } catch (error) {
        console.error('❌ Error in loadOwnersAndProperties:', error);
      }
    };
    
    if (user?.id) { loadOwnersAndProperties(); }
  }, [user?.id]); // Run when user changes

  // Initialize Clara's Voice System
  useEffect(() => {
    const initSpeechOutput = () => {
      if ('speechSynthesis' in window) {
        const loadVoices = () => {
          const voices = speechSynthesis.getVoices();
          const germanFemaleVoices = voices.filter(voice => 
            voice.lang.startsWith('de') && 
            (voice.name.toLowerCase().includes('female') || 
             voice.name.toLowerCase().includes('clara') ||
             voice.name.toLowerCase().includes('anna') ||
             voice.name.toLowerCase().includes('google') ||
             voice.name.toLowerCase().includes('deutsch'))
          ).slice(0, 3);
          
          setAvailableVoices(germanFemaleVoices);
          if (germanFemaleVoices.length > 0) {
            setSelectedVoice(germanFemaleVoices[0]);
          }
        };
        loadVoices();
        speechSynthesis.onvoiceschanged = loadVoices;
      }
    };
    initSpeechOutput();
  }, []);
  // Load owners and properties data from Supabase (1. August 2025)

  // SCHRITT 3A: AUTO-RESTART NACH IDLE - ENTFERNT FÜR MANUELLE KONTROLLE
  // useEffect(() => {
  //   if (hasVoicePermission && voiceState === 'idle' && !consentLoading) {
  //     // Auto-restart listening after 2 seconds in idle
  //     const restartTimer = setTimeout(() => {
  //       if (voiceState === 'idle' && hasVoicePermission) {
  //         setVoiceState('listening');
  //       }
  //     }, 2000);
  //     
  //     return () => clearTimeout(restartTimer);
  //   }
  // }, [hasVoicePermission, voiceState, consentLoading]);

  // Request microphone permission
  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  };

  // Clean stop recognition
  const stopRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.warn('Error stopping recognition:', error);
      }
      recognitionRef.current = null;
    }
  }, []);
  // Load owners and properties data from Supabase (1. August 2025)

  // Start recognition
  const startRecognition = useCallback(async () => {
    if (!hasPermission) {
      const granted = await requestMicrophonePermission();
      if (!granted) return false;
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      return false;
    }

    stopRecognition();

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'de-DE';
      recognitionRef.current.maxAlternatives = 1;
      
      recognitionRef.current.onstart = () => {
        console.log('🎤 Speech recognition started');
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        
        if (finalTranscript) {
          console.log('🎤 Final transcript:', finalTranscript);
          
          const lowerTranscript = finalTranscript.toLowerCase();
          if (lowerTranscript.includes('hey clara') || lowerTranscript.includes('hei clara') || lowerTranscript.includes('hey klara')) {
            handleWakeWordDetected(finalTranscript);
          } else if (voiceState === 'waiting' || voiceState === 'asking') {
            // User responded during waiting period
            handleFollowUpInput(finalTranscript);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          setHasPermission(false);
          setVoiceState('idle');
        }
      };

      recognitionRef.current.onend = () => {
        console.log('🎤 Speech recognition ended');
        // Don't auto-restart - let state management handle it
      };

      recognitionRef.current.start();
      return true;
    } catch (error) {
      console.error('Failed to start recognition:', error);
      return false;
    }
  }, [hasPermission, voiceState, stopRecognition]);

  // Handle voice state changes
  useEffect(() => {
    if (voiceState === 'listening' && hasVoicePermission) {
      startRecognition();
    } else if (voiceState === 'idle') {
      stopRecognition();
      clearTimers();
    }

    return () => {
      if (voiceState === 'idle') {
        stopRecognition();
        clearTimers();
      }
    };
  }, [voiceState, hasVoicePermission, startRecognition, stopRecognition]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
    setCountdownProgress(0);
  }, []);
  // Load owners and properties data from Supabase (1. August 2025)

  // Start countdown timer
  const startCountdown = useCallback((duration = 5000, onComplete) => {
    clearTimers();
    setCountdownProgress(100);
    
    const startTime = Date.now();
    countdownTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.max(0, 100 - (elapsed / duration) * 100);
      setCountdownProgress(progress);
      
      if (progress <= 0) {
        clearTimers();
        onComplete();
      }
    }, 50); // Update every 50ms for smooth animation
  }, [clearTimers]);

  // Clara's Speech Function
  const speakText = useCallback((text) => {
    if (!('speechSynthesis' in window) || !selectedVoice) return;
    
    speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.lang = 'de-DE';
    utterance.rate = 1.1;
    utterance.volume = 0.9;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      // SCHRITT 3B: OPTIMIERTER VOICE STATE FLOW
      if (voiceState === 'responding') {
        setVoiceState('waiting');
        startCountdown(5000, () => {
          // After 5 seconds, ask if user has more questions
          setVoiceState('asking');
          speakText("Haben Sie noch Fragen? Ansonsten ziehe ich mich zurück.");
          
          // Wait another 5 seconds for response, then auto-restart
          waitingTimerRef.current = setTimeout(() => {
            setVoiceState('idle'); // Will trigger auto-restart after 2 seconds
          }, 5000);
        });
      } else if (voiceState === 'asking') {
        // After asking, go to idle and auto-restart
        setVoiceState('idle');
      }
    };
    utterance.onerror = () => setIsSpeaking(false);
    
    speechSynthesis.speak(utterance);
  }, [selectedVoice, voiceState, startCountdown]);

  // Handle "Hey Clara" Wake Word
  const handleWakeWordDetected = async (fullTranscript) => {
    console.log('🎉 Hey Clara detected:', fullTranscript);
    setVoiceState('responding');
    
    const command = fullTranscript.toLowerCase()
      .replace('hey clara', '')
      .replace('hei clara', '')
      .replace('hey klara', '')
      .trim();
    
    // SCHRITT 2B: ECHTE KI STATT DUMMY-LOGIC
    await processWithClaraKI(command);
  };

  // Handle follow-up input during waiting/asking
  const handleFollowUpInput = async (input) => {
    clearTimers();
    setVoiceState('responding');
    await processWithClaraKI(input);
  };

  // SCHRITT 2B: ECHTE CLARA KI PROCESSING
  const processWithClaraKI = async (userInput) => {
    try {
      const result = await claraIntelligenceRef.current.processUserInput(userInput, {
        selectedOwner,
        selectedProperty,
        user: user?.id
      });
      
      console.log('🧠 Clara Intelligence Response:', result);
      
      // Speak the intelligent response
      speakText(result.response);
      
      // Optional: Execute actions based on Clara's response
      if (result.action && result.confidence > 0.7) {
        executeAction(result.action);
      }
      
    } catch (error) {
      console.error('Clara KI processing error:', error);
      speakText("Entschuldigung, ich hatte ein technisches Problem. Können Sie das wiederholen?");
    }
  };

  // Execute actions based on Clara's intelligence
  const executeAction = (action) => {
    switch (action) {
      case 'show_properties':
        // Could navigate to properties page or highlight properties section
        console.log('Action: Show properties');
        break;
      case 'show_tenants':
        console.log('Action: Show tenants');
        break;
      case 'show_finances':
        console.log('Action: Show finances');
        break;
      case 'show_maintenance':
        console.log('Action: Show maintenance');
        break;
      default:
        console.log('Action:', action);
    }
  };

  // Handle Clara Action Button Click
  const handleClaraActionClick = async () => {
    if (needsConsent) return;

    if (consentStatus === false) {
      try {
        const granted = await requestMicrophonePermission();
        if (granted) {
          setVoiceState('listening');
          speakText("Ich höre zu. Sagen Sie Hey Clara.");
        }
      } catch (error) {
        console.error('Permission error:', error);
      }
      return;
    }

    // Toggle voice state
    if (voiceState === 'idle') {
      setVoiceState('listening');
      speakText("Ich höre zu. Sagen Sie Hey Clara.");
      setVoiceState('idle');
      speechSynthesis.cancel();
    }
  };

  // Load properties for dropdown
  useEffect(() => {
    const loadProperties = async () => {
      try {
        let query = masterSupabase
          .from('properties')
          .select("id, property_name, street, owner_id")
          .order("property_name");

        if (selectedOwner !== 'all' && isValidUUID(selectedOwner)) {
          query = query.eq('owner_id', selectedOwner);
        }

        const { data: propertiesData, error } = await query;
        
        if (error) {
          console.error("Error loading properties:", error);
          return;
        }
        
        setProperties(propertiesData || []);
        
        if (selectedOwner !== 'all' && selectedProperty !== 'all') {
          const propertyExists = propertiesData?.some(p => p.id === selectedProperty);
          if (!propertyExists) {
            setSelectedProperty('all');
            setSelectedPropertyName(t('filters.allProperties'));
          }
        }
      } catch (error) {
        console.error("Error loading properties:", error);
      }
    };

    loadProperties();
  }, [selectedOwner, setProperties, selectedProperty, setSelectedProperty]);

  // Update selected owner name
  useEffect(() => {
    if (selectedOwner === 'all') {
      setSelectedOwnerName(t('filters.allOwners'));
      const owner = (owners || []).find(o => o.id === selectedOwner);
      setSelectedOwnerName(owner ? owner.name : t('filters.allOwners'));
    }
  }, [selectedOwner, owners]);

  // Update selected property name
  useEffect(() => {
    if (selectedProperty === 'all') {
      setSelectedPropertyName(t('filters.allProperties'));
      const property = (properties || []).find(p => p.id === selectedProperty);
      setSelectedPropertyName(property ? property.property_name : t('filters.allProperties'));
    }
  }, [selectedProperty, properties]);

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = currentTime.getHours();
    
    if (hour >= 6 && hour < 12) {
      return { greeting: t("header.greeting.morning"), icon: Sun, iconColor: 'text-yellow-500' };
    } else if (hour >= 12 && hour < 18) {
      return { greeting: t("header.greeting.afternoon"), icon: Sun, iconColor: 'text-yellow-500' };
    } else {
      return { greeting: t("header.greeting.evening"), icon: Moon, iconColor: 'text-blue-400' };
    }
  };

  const { greeting, icon: TimeIcon, iconColor } = getTimeBasedGreeting();

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Handle owner selection
  const handleOwnerSelect = (ownerId, ownerName) => {
    setSelectedOwner(ownerId);
    setSelectedOwnerName(ownerName);
    setIsDropdownOpen(false);
    
    localStorage.setItem('selectedOwner', ownerId);
    localStorage.setItem('selectedOwnerName', ownerName);
    
    window.dispatchEvent(new CustomEvent('ownerChanged', {
      detail: { ownerId, ownerName }
    }));
  };


  // Handle property selection
  const handlePropertySelect = (propertyId, propertyName) => {
    setSelectedProperty(propertyId);
    setSelectedPropertyName(propertyName);
    setIsPropertyDropdownOpen(false);
    
    localStorage.setItem('selectedProperty', propertyId);
    localStorage.setItem('selectedPropertyName', propertyName);
    
    window.dispatchEvent(new CustomEvent('propertyChanged', {
      detail: { propertyId, propertyName }
    }));
  };

  // Handle language selection
  const handleLanguageSelect = (languageCode) => {
    setCurrentLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    setIsLanguageDropdownOpen(false);
    
    localStorage.setItem('selectedLanguage', languageCode);
    
    window.dispatchEvent(new CustomEvent('languageChanged', {
      detail: { languageCode }
    }));
  };

  // Get current language flag with direct SVG implementation
  const getCurrentLanguageFlag = () => {
    const languages = {
      'de': { 
        svg: (
          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
            <rect width="20" height="5" fill="#000000"/>
            <rect width="20" height="5" y="5" fill="#DD0000"/>
            <rect width="20" height="5" y="10" fill="#FFCE00"/>
          </svg>
        ), 
        text: 'DE', 
        name: 'Deutsch' 
      },
      'en': { 
        svg: (
          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
            <rect width="20" height="15" fill="#B22234"/>
            <rect width="20" height="1.15" y="1.15" fill="white"/>
            <rect width="20" height="1.15" y="3.46" fill="white"/>
            <rect width="20" height="1.15" y="5.77" fill="white"/>
            <rect width="20" height="1.15" y="8.08" fill="white"/>
            <rect width="20" height="1.15" y="10.38" fill="white"/>
            <rect width="20" height="1.15" y="12.69" fill="white"/>
            <rect width="8" height="7" fill="#3C3B6E"/>
          </svg>
        ), 
        text: 'EN', 
        name: 'English' 
      }, 
      'es': { 
        svg: (
          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
            <rect width="20" height="15" fill="#AA151B"/>
            <rect width="20" height="9" y="3" fill="#F1BF00"/>
          </svg>
        ), 
        text: 'ES', 
        name: 'Español' 
      }
    };
    const lang = languages[currentLanguage] || languages['de'];
    return { svg: lang.svg, text: lang.text, name: lang.name };
  };

  // Load saved selections
  useEffect(() => {
    const savedOwner = localStorage.getItem('selectedOwner');
    const savedOwnerName = localStorage.getItem('selectedOwnerName');
    const savedProperty = localStorage.getItem('selectedProperty');
    const savedPropertyName = localStorage.getItem('selectedPropertyName');
    const savedLanguage = localStorage.getItem('selectedLanguage');
    
    if (savedOwner && savedOwnerName) {
      setSelectedOwner(savedOwner);
      setSelectedOwnerName(savedOwnerName);
    }
    
    if (savedProperty && savedPropertyName) {
      setSelectedProperty(savedProperty);
      setSelectedPropertyName(savedPropertyName);
    }
    
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);
  // Load owners and properties data from Supabase (1. August 2025)

  // Get status text based on voice state
  const getStatusText = () => {
    switch (voiceState) {
      case 'listening':
        return hasVoicePermission ? t('header.status.sayHeyClara') : t('header.status.clickMicrophone');
      case 'responding':
        return t('header.status.claraResponding');
      case 'waiting':
        return t('header.status.stillListening');
      case 'asking':
        return t('header.status.moreQuestions');
      default:
        return t('header.status.sayHeyClara');
    }
  };

  return (
    <>
      <div className="mb-8 pt-4 md:pt-6">  {/* Dashboard Header with Owner and Property Dropdowns */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-blue-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Dashboard
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <p className="text-gray-600 dark:text-gray-300">
                Übersicht über Ihre Immobilien, Mieten und KPIs
              </p>
              
              {/* Owner Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span>{selectedOwnerName}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-[9999]">
                    <div className="py-1">
                      <button
                        onClick={() => handleOwnerSelect('all', t('filters.allOwners'))}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          selectedOwner === 'all' 
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {t("filters.allOwners")}
                      </button>
                      {(owners || []).map((owner) => (
                        <button
                          key={owner.id}
                          onClick={() => handleOwnerSelect(owner.id, owner.name)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                            selectedOwner === owner.id 
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                              : 'text-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {owner.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Property Dropdown - SCHRITT 4B: BLACKSCREEN REPARIERT */}
              <div className="relative">
                <button
                  onClick={() => setIsPropertyDropdownOpen(!isPropertyDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span>{selectedPropertyName}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isPropertyDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-[9999]">
                    <div className="py-1">
                      <button
                        onClick={() => handlePropertySelect("all", t("filters.allProperties"))}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          selectedProperty === "all" 
                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" 
                            : "text-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {t("filters.allProperties")}
                      </button>
                      {(properties || []).map((property) => (
                        <button
                          key={property.id}
                          onClick={() => handlePropertySelect(property.id, property.property_name)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                            selectedProperty === property.id 
                              ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" 
                              : "text-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {property.property_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Performance Report Button */}
              <button
                onClick={() => navigate('/performance-report')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                title="Performance Report öffnen"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Performance Report</span>
              </button>
            </div>
          </div>
          
          {/* Notification Bell */}
          <div className="flex items-center">
            <button className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              <Bell className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Welcome Panel */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-xl p-6 text-white relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TimeIcon className={`w-6 h-6 ${iconColor}`} />
              <div>
                <h2 className="text-xl font-semibold">{greeting}</h2>
                <p className="text-blue-100 dark:text-blue-200">
                  {t("header.claraReady")} - {getStatusText()}
                </p>
              </div>
            </div>
            
            {/* Clara Action Button with Smart States */}
            <div className="flex items-center justify-center relative">
              {/* Konzentrische Wellen - nur bei listening/waiting */}
              {(voiceState === 'listening' || voiceState === 'waiting' || voiceState === 'asking') && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {[1, 2, 3].map(i => (
                    <div 
                      key={i}
                      className="absolute rounded-full border-2 border-pink-400/50 animate-ping"
                      style={{
                        width: `${50 + i * 20}px`,
                        height: `${50 + i * 20}px`,
                        animationDelay: `${i * 0.8}s`,
                        animationDuration: '4s'
                      }}
                    />
                  ))}
                </div>
              )}
              
              {/* Countdown Ring - nur bei waiting/asking */}
              {(voiceState === 'waiting' || voiceState === 'asking') && countdownProgress > 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth="2"
                      fill="none"
                      strokeDasharray={`${(countdownProgress / 100) * 226.19} 226.19`}
                      className="transition-all duration-75 ease-linear"
                    />
                  </svg>
                </div>
              )}
              
              {/* SCHRITT 4A: BUTTON CURSOR REPARIERT */}
              <button 
                onClick={handleClaraActionClick}
                className="relative group transform transition-all duration-300 hover:scale-110 select-none"
                style={{
                  outline: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  cursor: 'pointer' // EXPLIZIT GESETZT
                }}
                tabIndex={-1}
                contentEditable={false}
                suppressContentEditableWarning={true}
              >
                {/* 3D Schatten-Layer */}
                <div className={`absolute inset-0 bg-gradient-to-r ${
                  voiceState !== 'idle' ? 'from-pink-600/40 to-rose-600/40' : 'from-blue-600/40 to-purple-600/40'
                } rounded-full blur-lg opacity-60 group-hover:opacity-80 transition-all duration-500 transform translate-y-1`} />
                
                {/* Haupt-Button mit State-abhängigen Effekten */}
                <div className={`relative ${
                  voiceState === 'responding' ? 'bg-purple-500/20' :
                  voiceState !== 'idle' ? 'bg-pink-500/20' : 'bg-white/10'
                } backdrop-blur-md border ${
                  voiceState !== 'idle' ? 'border-pink-400/30' : 'border-white/20'
                } rounded-full p-4 shadow-[0_12px_40px_rgba(0,0,0,0.4),0_6px_20px_rgba(0,0,0,0.3),0_2px_8px_rgba(0,0,0,0.2),inset_0_2px_4px_rgba(255,255,255,0.1),inset_0_-2px_4px_rgba(0,0,0,0.2)] hover:bg-white/15 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)] cursor-pointer`}>
                  <Mic 
                    className={`w-6 h-6 ${
                      voiceState === 'responding' ? 'text-purple-300' :
                      voiceState !== 'idle' ? 'text-pink-300 animate-pulse' : 'text-pink-300'
                    } transition-all duration-300 group-hover:scale-110 select-none pointer-events-none`}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      cursor: 'pointer'
                    }}
                  />
                </div>
                
                {/* Glow-Effekt */}
                <div className={`absolute inset-0 rounded-full ${
                  voiceState === 'responding' ? 'bg-gradient-to-r from-purple-400/15 via-pink-400/15 to-purple-400/15' :
                  voiceState !== 'idle' ? 'bg-gradient-to-r from-pink-400/15 via-rose-400/15 to-pink-400/15' : 
                  'bg-gradient-to-r from-blue-400/15 via-purple-400/15 to-pink-400/15'
                } blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700`} />
                
                {/* Puls-Effekt beim Hover */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300" />
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-blue-100 dark:text-blue-200">Aktuelle Zeit</p>
                <p className="text-lg font-mono">{formatTime(currentTime)}</p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Language Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                    className="flex items-center justify-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm min-w-[44px] min-h-[36px]"
                    title="Sprache wechseln"
                  >
                    {getCurrentLanguageFlag().svg}
                  </button>
                  
                  {isLanguageDropdownOpen && (
                    <div 
                      className="absolute top-full right-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
                      style={{ 
                        zIndex: 99999,
                        position: 'fixed',
                        top: '60px',
                        right: '20px'
                      }}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => handleLanguageSelect('de')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-200 flex items-center gap-2"
                        >
                          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
                            <rect width="20" height="5" fill="#000000"/>
                            <rect width="20" height="5" y="5" fill="#DD0000"/>
                            <rect width="20" height="5" y="10" fill="#FFCE00"/>
                          </svg>
                          <span>Deutsch</span>
                        </button>
                        <button
                          onClick={() => handleLanguageSelect('en')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-200 flex items-center gap-2"
                        >
                          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
                            <rect width="20" height="15" fill="#B22234"/>
                            <rect width="20" height="1.15" y="1.15" fill="white"/>
                            <rect width="20" height="1.15" y="3.46" fill="white"/>
                            <rect width="20" height="1.15" y="5.77" fill="white"/>
                            <rect width="20" height="1.15" y="8.08" fill="white"/>
                            <rect width="20" height="1.15" y="10.38" fill="white"/>
                            <rect width="20" height="1.15" y="12.69" fill="white"/>
                            <rect width="8" height="7" fill="#3C3B6E"/>
                          </svg>
                          <span>English</span>
                        </button>
                        <button
                          onClick={() => handleLanguageSelect('es')}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-200 flex items-center gap-2"
                        >
                          <svg width="20" height="15" viewBox="0 0 20 15" style={{ borderRadius: '2px' }}>
                            <rect width="20" height="15" fill="#AA151B"/>
                            <rect width="20" height="9" y="3" fill="#F1BF00"/>
                          </svg>
                          <span>Español</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Clara Chat Button */}
                <button className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm font-medium">
                  Clara Chat
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Click outside to close dropdowns */}
        {(isDropdownOpen || isLanguageDropdownOpen || isPropertyDropdownOpen) && (
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => {
              setIsDropdownOpen(false);
              setIsLanguageDropdownOpen(false);
              setIsPropertyDropdownOpen(false);
            }}
          />
        )}
      </div>

      {/* Voice Consent Modal */}
      <VoiceConsentModal
        isOpen={showModal}
        onAccept={handleAccept}
        onDecline={handleDecline}
        onClose={closeModal}
      />
    </>
  );
};


      {/* DEBUG INFO - VISIBLE IN UI */}
      <div className="fixed bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-50">
        ClaraWelcomeHeader v20250903_111353
      </div>
export default ClaraWelcomeHeader;

// Cache bust Dashboard Fix Wed Sep  3 11:11:08 UTC 2025
